import React, { useState } from "react";
import { Modal, Button, Space, Typography, Table, Input, Form, Divider, Popconfirm, Tag, Tooltip, notification } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
// import { usePermissions } from "../../../PermissionsProvider";

const { Title } = Typography;

interface RegexReplacement {
  id: string;
  pattern: string;
  replacement: string;
  matchCount?: number;
  example?: string;
  exampleReplaced?: string;
}

interface DatasetModificationProps {
  datasetId: number;
  isVisible: boolean;
  onClose: () => void;
}

const DatasetModification: React.FC<DatasetModificationProps> = ({
  datasetId,
  isVisible,
  onClose,
}) => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [regexReplacements, setRegexReplacements] = useState<RegexReplacement[]>([]);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState<string>("");
  const [testingPattern, setTestingPattern] = useState<string>("");

  const isEditing = (record: RegexReplacement) => record.id === editingKey;

  const handleCancel = () => {
    setIsLoading(false);
    console.log(`Canceling dataset modification for dataset ${datasetId}`);
    onClose();
  };

  const handleAddRegexReplacement = () => {
    const newId = `regex-${Date.now()}`;
    const newReplacement: RegexReplacement = {
      id: newId,
      pattern: "",
      replacement: "",
    };
    setRegexReplacements([...regexReplacements, newReplacement]);
    setEditingKey(newId);
  };

  const handleDeleteRegexReplacement = (id: string) => {
    setRegexReplacements(regexReplacements.filter(item => item.id !== id));
  };

  const handleEditRegexReplacement = (record: RegexReplacement) => {
    form.setFieldsValue({ pattern: record.pattern, replacement: record.replacement });
    setEditingKey(record.id);
  };

  const handleSaveRegexReplacement = async (id: string) => {
    try {
      const row = await form.validateFields();
      const newData = [...regexReplacements];
      const index = newData.findIndex(item => id === item.id);
      
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });
        setRegexReplacements(newData);
        setEditingKey("");
        
        // Test the pattern after saving
        testRegexPattern(row.pattern, row.replacement, id);
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleCancelEdit = () => {
    // If it's a new empty row and user cancels, remove it
    if (editingKey && regexReplacements.find(r => r.id === editingKey)?.pattern === "") {
      setRegexReplacements(regexReplacements.filter(item => item.id !== editingKey));
    }
    setEditingKey("");
  };

  const testRegexPattern = async (pattern: string, replacement: string, id?: string) => {
    try {
      setTestingPattern(id || "all");
      DatasetService.getRegexReplacementsApplicableCount(
        datasetId,
        pattern,
        replacement,
        () => {},
        apiKey
      ).subscribe(
        (response) => {
          // Make sure we're handling the response correctly
          const matchCount = response.applicable_request_count;
          const example = response.example;
          const exampleReplaced = response.example_replaced;
          
          if (id) {
            // Update specific replacement with the count and examples
            setRegexReplacements(prev => 
              prev.map(item => 
                item.id === id 
                  ? { ...item, matchCount: matchCount, example: example, exampleReplaced: exampleReplaced } 
                  : item
              )
            );
          }
          
          setTestingPattern("");
        },
        (error) => {
          console.error("Error testing regex pattern:", error);
          setTestingPattern("");
        }
      );
    } catch (error) {
      console.error("Error testing regex pattern:", error);
      setTestingPattern("");
    }
  };

  const handleSubmit = () => {
    if (regexReplacements.length === 0) {
      notification.warning({
        message: "No modifications to apply",
        description: "Please add at least one regex replacement rule before applying modifications.",
      });
      return;
    }

    // Check if any regex replacement is being edited
    if (editingKey !== "") {
      notification.warning({
        message: "Unsaved changes",
        description: "Please save or cancel your current edits before applying modifications.",
      });
      return;
    }

    setIsLoading(true);

    // Format the regex replacements for the API
    const formattedReplacements = regexReplacements.map(({ pattern, replacement }) => ({
      pattern,
      replacement,
    }));

    DatasetService.applyRegexReplacements(
      datasetId,
      formattedReplacements,
      setIsLoading,
      apiKey
    ).subscribe(
      () => {
        notification.success({
          message: "Modifications Applied",
          description: "The regex replacements have been successfully applied to the dataset.",
        });
        setIsLoading(false);
        onClose();
      },
      (error) => {
        console.error("Error applying regex replacements:", error);
        notification.error({
          message: "Failed to Apply Modifications",
          description: error.message || "An error occurred while applying regex replacements.",
        });
        setIsLoading(false);
      }
    );
  };

  const columns = [
    {
      title: "Pattern (Regex)",
      dataIndex: "pattern",
      key: "pattern",
      editable: true,
      render: (_: any, record: RegexReplacement) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="pattern"
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Please input a regex pattern" }]}
            tooltip="Use PostgreSQL regex syntax. Example: .*:\\s*(yes|no).* to capture 'yes' or 'no' after a colon"
          >
            <Input placeholder="e.g., .*:\\s*(yes|no).*" />
          </Form.Item>
        ) : (
          <span>{record.pattern || "-"}</span>
        );
      },
    },
    {
      title: "Replacement",
      dataIndex: "replacement",
      key: "replacement",
      editable: true,
      render: (_: any, record: RegexReplacement) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="replacement"
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Please input a replacement" }]}
            tooltip="Use $1, $2, etc. to reference captured groups from your pattern"
          >
            <Input placeholder="e.g., $1" />
          </Form.Item>
        ) : (
          <span>{record.replacement || "-"}</span>
        );
      },
    },
    {
      title: "Matches",
      dataIndex: "matchCount",
      key: "matchCount",
      render: (_: any, record: RegexReplacement) => {
        const isLoading = testingPattern === record.id;
        
        if (isLoading) {
          return <span>Testing...</span>;
        }
        
        if (record.matchCount !== undefined) {
          // Ensure matchCount is a number, not an object
          const count = typeof record.matchCount === "object" 
            ? 0 
            : record.matchCount;
            
          const tooltipContent = record.example && record.exampleReplaced ? (
            <div>
              <div><strong>Before:</strong> {record.example}</div>
              <div><strong>After:</strong> {record.exampleReplaced}</div>
            </div>
          ) : null;

          return (
            <Tooltip title={tooltipContent}>
              <Tag color={count > 0 ? "green" : "orange"}>
                {count} {count === 1 ? "match" : "matches"}
              </Tag>
            </Tooltip>
          );
        }
        
        return (
          <Button 
            type="link" 
            size="small" 
            onClick={() => testRegexPattern(record.pattern, record.replacement, record.id)}
            disabled={!record.pattern || editingKey !== "" || isLoading}
          >
            Test
          </Button>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: RegexReplacement) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button type="link" onClick={() => handleSaveRegexReplacement(record.id)} disabled={isLoading}>
              Save
            </Button>
            <Button type="link" onClick={handleCancelEdit} disabled={isLoading}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Space>
            <Button 
              type="link" 
              disabled={editingKey !== "" || isLoading} 
              onClick={() => handleEditRegexReplacement(record)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this rule?"
              onConfirm={() => handleDeleteRegexReplacement(record.id)}
              okText="Yes"
              cancelText="No"
              disabled={isLoading}
            >
              <Button type="link" danger disabled={editingKey !== "" || isLoading}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={<Title level={4}>Dataset Modification</Title>}
      open={isVisible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          Apply Modifications
        </Button>,
      ]}
      width={800}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Divider orientation="left">
          <Tooltip title="Regex replacements are applied to all responses in the dataset that match the pattern. For example, if you want to convert 'foo: bar' to just 'bar', you can use a pattern like '.*:\\s*(bar).*' with replacement '$1'. The replacement can use capture groups from the pattern.">
            Regex Replacements <InfoCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        </Divider>
        
        <Form form={form} component={false}>
          <Table
            dataSource={regexReplacements}
            columns={columns}
            rowKey="id"
            pagination={false}
            rowClassName="editable-row"
            bordered
          />
        </Form>
        
        <Button 
          type="dashed" 
          onClick={handleAddRegexReplacement} 
          style={{ marginTop: 16 }}
          disabled={editingKey !== "" || isLoading}
        >
          + Add Regex Replacement
        </Button>
      </Space>
    </Modal>
  );
};

export default DatasetModification; 